import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import ModelerX6 from '@core_x6/lib/ModelerX6';
import {getPictoGroupingSvgD} from '@core_x6/lib/draw/PathMap';
import {OR_JUNCTION} from '@core_x6/lib/modelerX6/constants';
import {normalizeType} from '@core_x6/lib/modelerX6/viewHelpers';

/** Минимум API ModelerX6 для иконок палитры */
export type PaletteModelerLike = {
	getIconColorsForType?: (elementType: string) => {fillColor: string; lineColor: string};
	getIconSvgPath?: (iconRef: string | null, width?: number, height?: number) => string | null;
};

type PaletteEntry = {
	type: string;
	typeName: string;
	width: number;
	height: number;
	iconRef: string | null;
	shape: string;
};

/** Как в ArchiX6Palette — SVG из public/archi-relation-icons */
const RELATION_ICON_NAMES = [
	'association',
	'access',
	'flow',
	'serving',
	'influence',
	'realization',
	'aggregation',
	'composition',
	'assignment',
	'specialization',
	'triggering',
] as const;

const RELATION_ICON_EXT = '.svg';

function buildPaletteEntryByType(): Map<string, PaletteEntry> {
	const m = new Map<string, PaletteEntry>();
	for (const g of ModelerX6.getPaletteGroups()) {
		for (const e of g.entries) {
			m.set(e.type, e);
			const nt = normalizeType(e.type);
			if (nt && nt !== e.type) {
				m.set(nt, e);
			}
		}
	}
	if (!m.has(OR_JUNCTION)) {
		const or: PaletteEntry = {
			type: OR_JUNCTION,
			typeName: 'OrJunction',
			width: 14,
			height: 14,
			iconRef: null,
			shape: 'junction-or',
		};
		m.set(OR_JUNCTION, or);
		m.set(normalizeType(OR_JUNCTION), or);
	}
	return m;
}

const PALETTE_ENTRY_BY_TYPE = buildPaletteEntryByType();

function resolvePaletteEntry(archimateType: string): PaletteEntry | undefined {
	const raw = archimateType?.trim() ?? '';
	if (!raw) return undefined;
	return PALETTE_ENTRY_BY_TYPE.get(raw) ?? PALETTE_ENTRY_BY_TYPE.get(normalizeType(raw));
}

function PalettePathIcon({
	path,
	size,
	colors,
	svgStyle,
}: {
	path: string;
	size: number;
	colors: {fillColor: string; lineColor: string};
	svgStyle?: React.CSSProperties;
}) {
	const gRef = useRef<SVGGElement | null>(null);
	const [transform, setTransform] = useState('');
	useLayoutEffect(() => {
		const g = gRef.current;
		if (!g) return;
		const pathEl = g.querySelector('path');
		if (!pathEl) return;
		try {
			const bbox = pathEl.getBBox();
			if (bbox.width <= 0 || bbox.height <= 0) return;
			const pad = 1;
			const scale = Math.min((size - pad * 2) / bbox.width, (size - pad * 2) / bbox.height);
			const cx = bbox.x + bbox.width / 2;
			const cy = bbox.y + bbox.height / 2;
			setTransform(
				`translate(${size / 2},${size / 2}) scale(${scale}) translate(${-cx},${-cy})`
			);
		} catch {
			// getBBox can fail in some envs
		}
	}, [path, size]);

	return (
		<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={svgStyle}>
			<g ref={gRef} transform={transform}>
				<path
					d={path}
					fill={colors.fillColor === 'none' ? 'none' : colors.fillColor}
					stroke={colors.lineColor}
					strokeWidth={0.5}
				/>
			</g>
		</svg>
	);
}

function RelationshipMiniIcon({type, size}: {type: string; size: number}) {
	const iconName = type && typeof type === 'string' ? type.trim().toLowerCase() : '';
	const hasIcon = (RELATION_ICON_NAMES as readonly string[]).includes(iconName);
	if (!hasIcon) {
		return (
			<span
				style={{
					width: size,
					height: size,
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: Math.round(size * 0.55),
					color: '#666',
					flexShrink: 0,
				}}
			>
				{type?.toString().charAt(0) ?? '?'}
			</span>
		);
	}
	return (
		<img
			src={`/archi-relation-icons/${iconName}${RELATION_ICON_EXT}`}
			alt=""
			width={size}
			height={size}
			style={{flexShrink: 0, display: 'block', objectFit: 'contain'}}
		/>
	);
}

export type ArchimatePaletteMiniIconProps = {
	modeler: PaletteModelerLike | null | undefined;
	kind: 'node' | 'connection';
	archimateType: string;
	size?: number;
	/** Если modeler недоступен или нет path — запасной узел */
	fallback: React.ReactNode;
};

/**
 * Миниатюра как в боковой палитре (path + цвет слоя или иконка связи).
 */
const ArchimatePaletteMiniIcon: React.FC<ArchimatePaletteMiniIconProps> = ({
	modeler,
	kind,
	archimateType,
	size = 20,
	fallback,
}) => {
	const svgStyle = useMemo(
		() => ({flexShrink: 0, display: 'block', margin: 'auto'}) as React.CSSProperties,
		[]
	);

	if (kind === 'connection') {
		return <RelationshipMiniIcon type={archimateType} size={size} />;
	}

	if (!modeler?.getIconColorsForType || !modeler.getIconSvgPath) {
		return <>{fallback}</>;
	}

	const entry = resolvePaletteEntry(archimateType);
	const normalized = normalizeType(archimateType);
	const colors = modeler.getIconColorsForType(normalized || archimateType);

	if (entry?.shape === 'junction') {
		return (
			<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={svgStyle}>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={size / 2 - 2}
					fill={colors.fillColor === 'none' ? 'none' : colors.fillColor}
					stroke={colors.lineColor}
					strokeWidth={1}
				/>
			</svg>
		);
	}
	if (entry?.shape === 'junction-or') {
		return (
			<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={svgStyle}>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={size / 2 - 2}
					fill="none"
					stroke={colors.lineColor}
					strokeWidth={1}
				/>
			</svg>
		);
	}
	if (entry?.shape === 'group') {
		const path =
			modeler.getIconSvgPath?.('PICTO_GROUPING', size, size) ??
			getPictoGroupingSvgD(size, size);
		return <PalettePathIcon path={path} size={size} colors={colors} svgStyle={svgStyle} />;
	}
	if (entry?.shape === 'sticker') {
		const pad = 4;
		return (
			<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={svgStyle}>
				<rect
					x={pad + 1}
					y={pad + 1.5}
					width={size - pad * 2}
					height={size - pad * 2}
					rx={2}
					ry={2}
					fill="rgba(0,0,0,0.16)"
					stroke="none"
				/>
				<rect
					x={pad}
					y={pad}
					width={size - pad * 2}
					height={size - pad * 2}
					rx={2}
					ry={2}
					fill={colors.fillColor === 'none' ? '#FFF9B1' : colors.fillColor}
					stroke="none"
				/>
			</svg>
		);
	}

	const path = entry?.iconRef ? modeler.getIconSvgPath(entry.iconRef, size, size) : null;
	if (path) {
		return <PalettePathIcon path={path} size={size} colors={colors} svgStyle={svgStyle} />;
	}

	return <>{fallback}</>;
};

export default ArchimatePaletteMiniIcon;
